import React, { useState, useEffect } from "react";
import API_URL from "../config";
import {
  FaSpinner,
  FaPlusCircle,
  FaEdit,
  FaTrash,
  FaClipboard,
  FaShareAlt,
  FaWhatsapp,
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaUsers,
  FaTimes,
  FaShieldAlt
} from "react-icons/fa";
import { Clipboard } from "@capacitor/clipboard";
import { Share } from "@capacitor/share";
import "./Gestionnaire.css";

export default function Gestionnaire({ entrepriseId, siteId }) {
  const [gestionnaires, setGestionnaires] = useState([]);
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [lastCreatedEmail, setLastCreatedEmail] = useState("");

  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingGestionnaires, setLoadingGestionnaires] = useState(true);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: null,
  });

  const openDialog = (title, message, type = "info", onConfirm = null) =>
    setDialog({ open: true, title, message, type, onConfirm });
  const closeDialog = () =>
    setDialog((prev) => ({ ...prev, open: false, onConfirm: null }));

  const fetchGestionnaires = async () => {
    try {
      if (!entrepriseId || !siteId) {
        setGestionnaires([]);
        setLoadingGestionnaires(false);
        return;
      }
      setLoadingGestionnaires(true);
      const res = await fetch(
        `${API_URL}/api/gestionnaires?entreprise_id=${entrepriseId}&siteId=${siteId}`
      );
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.message || "Erreur de chargement");
      setGestionnaires(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur chargement:", err);
      openDialog("Erreur", "❌ Problème de connexion au serveur.", "error");
      setGestionnaires([]);
    } finally {
      setLoadingGestionnaires(false);
    }
  };

  useEffect(() => {
    fetchGestionnaires();
  }, [entrepriseId, siteId]);

  const handleSaveGestionnaire = async (e) => {
    e.preventDefault();
    if (!nom.trim() || !prenom.trim() || !email.trim() || !telephone.trim()) {
      openDialog("Attention", "⚠️ Tous les champs sont obligatoires.", "info");
      return;
    }

    const payload = {
      nom: nom.trim(),
      prenom: prenom.trim(),
      email: email.trim(),
      telephone: telephone.trim(),
      entreprise_id: entrepriseId,
      siteId,
    };

    try {
      setLoadingSave(true);
      let res;
      if (editingId) {
        res = await fetch(`${API_URL}/api/gestionnaires/${editingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API_URL}/api/gestionnaires`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Erreur serveur");

      if (!editingId) {
        setLastCreatedEmail(payload.email);
        if (data?.password) setGeneratedPassword(data.password);
      }

      openDialog("Succès", editingId ? "✅ Modifié avec succès !" : "✅ Gestionnaire créé !", "success");
      handleCancelEdit();
      await fetchGestionnaires();
    } catch (err) {
      openDialog("Erreur", `❌ ${err.message}`, "error");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleEdit = (g) => {
    setEditingId(g.id ?? g._id ?? null);
    setNom(g.nom ?? "");
    setPrenom(g.prenom ?? "");
    setEmail(g.email ?? "");
    setTelephone(g.telephone ?? "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setNom("");
    setPrenom("");
    setEmail("");
    setTelephone("");
  };

  const handleDeleteGestionnaire = (g) => {
    const id = g.id ?? g._id;
    openDialog(
      "Confirmation",
      `Supprimer ${g.nom} ${g.prenom} ? Cette action est irréversible.`,
      "confirm",
      async () => {
        try {
          setLoadingDeleteId(id);
          const res = await fetch(
            `${API_URL}/api/gestionnaires/${id}?entreprise_id=${entrepriseId}&siteId=${siteId}`,
            { method: "DELETE" }
          );
          if (!res.ok) throw new Error("Erreur suppression");
          openDialog("Supprimé", "Le compte a été retiré.", "success");
          await fetchGestionnaires();
        } catch (err) {
          openDialog("Erreur", `❌ ${err.message}`, "error");
        } finally {
          setLoadingDeleteId(null);
        }
      }
    );
  };

  const copyPassword = async () => {
    const credentials = `🔐 Identifiants :\nEmail : ${lastCreatedEmail}\nPass : ${generatedPassword}`;
    await Clipboard.write({ string: credentials });
    openDialog("Copié", "📋 Identifiants copiés dans le presse-papier !", "success");
  };

  const shareGeneric = async () => {
    const message = `🔐 Accès Gestionnaire :\nEmail : ${lastCreatedEmail}\nMot de passe : ${generatedPassword}`;
    try {
      await Share.share({ title: "Accès Application", text: message });
    } catch {
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
    }
  };

  return (
    <div className="gestion-container">
      <header className="gestion-header">
        <div className="header-text">
          <h1>Gestionnaires</h1>
          <p>Équipe & Accès sécurisés</p>
        </div>
        <div className="header-badge">
          <FaUsers /> <span>{gestionnaires.length} Actifs</span>
        </div>
      </header>

      <section className="form-card">
        <div className="card-header-icon">
          <FaShieldAlt />
          <h2>{editingId ? "Modifier l'accès" : "Nouvel accès"}</h2>
        </div>
        
        <form onSubmit={handleSaveGestionnaire} className="gestion-form">
          <div className="input-grid">
            <div className="input-field">
              <FaUser className="input-icon" />
              <input type="text" placeholder="Nom" value={nom} onChange={(e) => setNom(e.target.value)} disabled={loadingSave} />
            </div>
            <div className="input-field">
              <FaUser className="input-icon" />
              <input type="text" placeholder="Prénom" value={prenom} onChange={(e) => setPrenom(e.target.value)} disabled={loadingSave} />
            </div>
            <div className="input-field">
              <FaEnvelope className="input-icon" />
              <input type="email" placeholder="Email professionnel" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loadingSave} />
            </div>
            <div className="input-field">
              <FaPhoneAlt className="input-icon" />
              <input type="tel" placeholder="Numéro de téléphone" value={telephone} onChange={(e) => setTelephone(e.target.value)} disabled={loadingSave} />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className={`btn-main ${editingId ? 'btn-edit' : 'btn-add'}`} disabled={loadingSave}>
              {loadingSave ? <FaSpinner className="spin" /> : editingId ? <FaEdit /> : <FaPlusCircle />}
              <span>{editingId ? "Mettre à jour" : "Créer le compte"}</span>
            </button>
            {editingId && (
              <button type="button" onClick={handleCancelEdit} className="btn-cancel">
                <FaTimes />
              </button>
            )}
          </div>
        </form>

        {generatedPassword && lastCreatedEmail && (
          <div className="credentials-box animate-pop">
            <div className="creds-header">🔐 Identifiants de connexion</div>
            <div className="creds-content">
              <div className="creds-item"><span>Email:</span> <strong>{lastCreatedEmail}</strong></div>
              <div className="creds-item"><span>Pass:</span> <strong className="pwd-text">{generatedPassword}</strong></div>
            </div>
            <div className="creds-actions">
              <button onClick={copyPassword} className="btn-action copy"><FaClipboard /> Copier</button>
              <button onClick={shareGeneric} className="btn-action share"><FaShareAlt /> Partager</button>
              <button onClick={shareGeneric} className="btn-whatsapp"><FaWhatsapp /> WhatsApp</button>
            </div>
          </div>
        )}
      </section>

      <section className="list-section">
        <div className="list-header">
          <h3>Collaborateurs enregistrés</h3>
        </div>
        
        {loadingGestionnaires ? (
          <div className="loader"><FaSpinner className="spin" /> Chargement de la liste...</div>
        ) : (
          <div className="table-wrapper">
            <table className="gestion-table">
              <thead>
                <tr>
                  <th>Collaborateur</th>
                  <th>Contact</th>
                  <th className="txt-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {gestionnaires.length === 0 ? (
                  <tr><td colSpan="3" className="empty-row">Aucun gestionnaire pour le moment</td></tr>
                ) : (
                  gestionnaires.map((g) => (
                    <tr key={g.id || g._id}>
                      <td data-label="Collaborateur">
                        <div className="user-info">
                          <div className="user-avatar">{g.nom.charAt(0)}</div>
                          <div>
                            <div className="user-name">{g.nom} {g.prenom}</div>
                            <div className="user-sub">Gestionnaire</div>
                          </div>
                        </div>
                      </td>
                      <td data-label="Contact">
                        <div className="contact-info">
                          <span>{g.email}</span>
                          <small>{g.telephone}</small>
                        </div>
                      </td>
                      <td data-label="Actions" className="txt-center">
                        <div className="action-icons">
                          <button className="icon-btn edit" title="Modifier" onClick={() => handleEdit(g)}><FaEdit /></button>
                          <button className="icon-btn delete" title="Supprimer" onClick={() => handleDeleteGestionnaire(g)}>
                            {loadingDeleteId === (g.id || g._id) ? <FaSpinner className="spin" /> : <FaTrash />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {dialog.open && (
        <div className="modal-overlay">
          <div className="modal-content animate-pop">
            <div className={`modal-header ${dialog.type}`}>
              <h3>{dialog.title}</h3>
            </div>
            <div className="modal-body">
              <p>{dialog.message}</p>
            </div>
            <div className="modal-footer">
              {dialog.type === "confirm" ? (
                <>
                  <button className="btn-modal confirm" onClick={() => { closeDialog(); dialog.onConfirm?.(); }}>Confirmer</button>
                  <button className="btn-modal cancel" onClick={closeDialog}>Annuler</button>
                </>
              ) : (
                <button className="btn-modal close" onClick={closeDialog}>D'accord</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}